import { router, useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Badge } from '../../../components/ui/badge'
import { Key, AlertCircle } from 'lucide-react'
import { FormEvent } from 'react'

interface CayuProvidedSecret {
  id: number
  service_name: string
  secret_name: string
  projects_count: number
  organizations_count: number
  created_at: string
  updated_at: string
}

interface AdminCayuProvidedSecretsEditProps {
  cayu_provided_secret: CayuProvidedSecret
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

const SERVICE_ICONS: Record<string, string> = {
  openai: '🤖',
  anthropic: '🧠',
  google_gemini: '✨',
  sendgrid: '📧',
}

const SERVICE_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google_gemini: 'Google Gemini',
  sendgrid: 'SendGrid',
}

function Edit({ cayu_provided_secret }: AdminCayuProvidedSecretsEditProps) {
  const { t } = useTranslation('admin')
  const { data, setData, patch, processing, errors } = useForm({
    secret_name: cayu_provided_secret.secret_name,
    value: '', // Don't pre-fill for security
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    const updateData: any = { secret_name: data.secret_name }
    if (data.value.trim()) {
      updateData.value = data.value
    }

    patch(`/admin/cayu_provided_secrets/${cayu_provided_secret.id}`, {
      data: updateData,
    } as any)
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Cayu Provided Keys', href: '/admin/cayu_provided_secrets' },
          { label: 'Edit' },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-4xl">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{SERVICE_ICONS[cayu_provided_secret.service_name]}</span>
                    <h1 className="text-3xl font-bold text-foreground">
                      {t('cayuProvidedKeys.edit.title', { service: SERVICE_NAMES[cayu_provided_secret.service_name] })}
                    </h1>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cayuProvidedKeys.edit.description')}
                  </p>
                </div>
              </div>

              {/* Edit Form */}
              <form onSubmit={handleSubmit} className="space-y-6 mb-6">
                <div className="bg-card rounded-lg border border-border p-6 space-y-6">
                  <div className="space-y-2">
                    <Label>{t('cayuProvidedKeys.form.service')}</Label>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                      <span>{SERVICE_ICONS[cayu_provided_secret.service_name]}</span>
                      <span className="font-medium">
                        {SERVICE_NAMES[cayu_provided_secret.service_name]}
                      </span>
                      <Badge variant="secondary" className="ml-2">{t('cayuProvidedKeys.form.readOnly')}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.form.serviceReadOnlyHint')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secret_name">
                      {t('cayuProvidedKeys.form.keyName')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="secret_name"
                      type="text"
                      placeholder={t('cayuProvidedKeys.form.keyNamePlaceholder')}
                      value={data.secret_name}
                      onChange={(e) => setData('secret_name', e.target.value)}
                      required
                    />
                    {errors.secret_name && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.secret_name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value">
                      {t('cayuProvidedKeys.form.apiKeyValue')} <span className="text-muted-foreground">({t('cayuProvidedKeys.form.optional')})</span>
                    </Label>
                    <Input
                      id="value"
                      type="password"
                      placeholder={t('cayuProvidedKeys.form.keepCurrentValue')}
                      value={data.value}
                      onChange={(e) => setData('value', e.target.value)}
                      className="font-mono text-sm"
                    />
                    {errors.value && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.value}
                      </p>
                    )}
                    <p className="text-sm text-amber-600">
                      ⚠️ {t('cayuProvidedKeys.form.syncWarning', { count: cayu_provided_secret.projects_count })}
                    </p>
                  </div>

                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Key className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-purple-900">{t('cayuProvidedKeys.security.title')}</h3>
                        <p className="text-sm text-purple-700 mt-1">
                          {t('cayuProvidedKeys.security.editKeyNote')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.visit('/admin/cayu_provided_secrets')}
                    disabled={processing}
                  >
                    {t('cayuProvidedKeys.actions.cancel')}
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? t('cayuProvidedKeys.actions.updating') : t('cayuProvidedKeys.actions.updateKey')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Edit.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Edit
